import { ApolloClient } from '@apollo/client';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  CreateEntityShortForm_HouseholdFragment,
  CreateEntityShortFormDataQuery,
} from '@/modules/entities/EntityShortFormModal/CreateEntityShortForm/graphql/CreateEntityShortForm.generated';
import { AssetTaxSitusFormValue } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import { buildAssetStaticSitusPatch } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { makeEntityTypeUniversalUpdateInput } from '@/modules/entities/utils/makeEntityTypeUniversalUpdateInput';
import {
  AugmentedCreateEntityInput,
  AugmentedUpdateEntityInput,
  EntityDocumentRelationshipRelationshipType,
} from '@/types/schema';

import { EntityShortFormShape } from './CreateEntityShortForm/CreateEntityShortForm.types';
import { MakeCreateEntityInputParams } from './CreateEntityShortForm/formUtils/CreateEntityShortForm.common';
import { makeCreateEntityInput } from './CreateEntityShortForm/formUtils/CreateEntityShortForm.entityUtils';
import {
  CreateEntityFromShortFormMutation,
  GetEntityForShortFormSitusUpdateDocument,
  GetEntityForShortFormSitusUpdateQuery,
  GetEntityForShortFormSitusUpdateQueryVariables,
} from './graphql/CreateEntityShortFormModal.generated';

export function mapFormDataToInput(
  formData: EntityShortFormShape,
  householdId: string,
  { associatedDocumentIds }: MakeCreateEntityInputParams
): AugmentedCreateEntityInput {
  const input = makeCreateEntityInput(formData, householdId, {
    associatedDocumentIds: [],
  });

  input.withEntityDocumentRelationships = [
    ...associatedDocumentIds.map((id) => ({
      create: {
        householdID: householdId,
        documentID: id,
        relationshipType: EntityDocumentRelationshipRelationshipType.Mentioned,
      },
    })),
  ];

  return input;
}

// Caller must pre-refetch the entity — assetStatic is null on the create response (BE OnCommit hook fires post-commit).
export function makeShortFormSitusUpdateInput(
  taxSitus: AssetTaxSitusFormValue | undefined,
  entityType: EntityType,
  createdEntity: CreateEntityFromShortFormMutation['createEntity']
): AugmentedUpdateEntityInput | null {
  if (!taxSitus) return null;

  const subtype = createdEntity.subtype;
  const designerAccount = subtype.designerAccount;
  const valuationNode = designerAccount?.valuations.edges?.[0]?.node;
  const assetNode = valuationNode?.assets.edges?.[0]?.node;
  const assetStaticId = assetNode?.assetStatic?.id;
  if (!designerAccount || !valuationNode || !assetNode || !assetStaticId) {
    return null;
  }

  return makeEntityTypeUniversalUpdateInput(createdEntity.id, entityType, {
    id: subtype.id,
    updateDesignerAccount: {
      id: designerAccount.id,
      update: {},
      updateValuations: [
        {
          id: valuationNode.id,
          update: {},
          updateAssets: [
            {
              id: assetNode.id,
              update: {},
              updateAssetStatic: {
                id: assetStaticId,
                update: buildAssetStaticSitusPatch(taxSitus),
              },
            },
          ],
        },
      ],
    },
  });
}

export type ShortFormSitusApplyResult =
  | { status: 'ok' }
  | { status: 'refetch-failed'; error: Error }
  | { status: 'missing-asset-static' }
  | { status: 'mutation-failed'; error: Error };

export async function applyShortFormSitusOverride({
  apolloClient,
  updateEntity,
  entityId,
  taxSitus,
  entityType,
}: {
  apolloClient: ApolloClient<object>;
  updateEntity: (input: AugmentedUpdateEntityInput) => Promise<unknown>;
  entityId: string;
  taxSitus: AssetTaxSitusFormValue;
  entityType: EntityType;
}): Promise<ShortFormSitusApplyResult> {
  let refetchResult;
  try {
    refetchResult = await apolloClient.query<
      GetEntityForShortFormSitusUpdateQuery,
      GetEntityForShortFormSitusUpdateQueryVariables
    >({
      query: GetEntityForShortFormSitusUpdateDocument,
      variables: { entityId },
      fetchPolicy: 'network-only',
    });
  } catch (error) {
    return { status: 'refetch-failed', error: error as Error };
  }

  const refetchedEntity = refetchResult.data.entities.edges?.[0]?.node;
  const situsUpdateInput = refetchedEntity
    ? makeShortFormSitusUpdateInput(taxSitus, entityType, refetchedEntity)
    : null;

  if (!situsUpdateInput) {
    return { status: 'missing-asset-static' };
  }

  try {
    await updateEntity(situsUpdateInput);
    return { status: 'ok' };
  } catch (error) {
    return { status: 'mutation-failed', error: error as Error };
  }
}

/**
 * Return options and other entity form configuration from the household data query.
 * @param data
 */
export function getFormConfigurationFromData(
  data: CreateEntityShortFormDataQuery | undefined
): { principalOptions: SelectInputOption<string>[] } {
  if (!data?.household) {
    return { principalOptions: [] };
  }
  const household = data.household as CreateEntityShortForm_HouseholdFragment;
  const principalOptions = household.possibleGrantors
    .map((grantor) => ({
      display: grantor.displayName,
      value: grantor.id,
    }))
    .sort((a, b) => a.display.localeCompare(b.display));
  return { principalOptions };
}
